import React, { useId } from 'react';

/**
 * Etiqueta + control de formulario, asociados entre sí.
 *
 * Existe para resolver un problema que estaba repetido en todo el sistema: cada
 * pantalla definía su propio envoltorio de campo, y en todos la etiqueta era
 * hermana del input sin `htmlFor`. El efecto práctico es que hacer clic en la
 * etiqueta no enfoca el campo, y un lector de pantalla no la anuncia al llegar
 * al input: solo dice "cuadro de edición", sin decir de qué.
 *
 * El identificador se genera solo con `useId`, de modo que no haya que
 * inventar nombres únicos ni arriesgar que se repitan entre dos formularios de
 * la misma página.
 *
 * Uso normal — el control es hijo directo:
 *
 *     <Field label="Nombre del negocio">
 *       <input name="nombre" value={...} onChange={...} />
 *     </Field>
 *
 * Cuando el input va envuelto (por ejemplo, con un botón de mostrar
 * contraseña), no hay forma de adivinar a qué asociar la etiqueta: en ese caso
 * se pasa `htmlFor` y se repite ese mismo id en el input.
 *
 *     <Field label="Contraseña" htmlFor="clave">
 *       <div className="relative">
 *         <input id="clave" type="password" />
 *         <button type="button">...</button>
 *       </div>
 *     </Field>
 */

// Elementos a los que una etiqueta puede apuntar sin ambigüedad.
const CONTROLES = ['input', 'select', 'textarea'];

export const ETIQUETA_POR_OMISION =
  'text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block px-1';

export default function Field({
  label,
  hint,
  htmlFor,
  className = '',
  labelClassName = ETIQUETA_POR_OMISION,
  hintClassName = 'text-[11px] text-slate-400 px-1',
  children,
}) {
  const idGenerado = useId();

  const hijo = React.isValidElement(children) ? children : null;
  const idDelHijo = hijo?.props?.id;
  const id = htmlFor || idDelHijo || idGenerado;

  const esControl = Boolean(hijo) && CONTROLES.includes(hijo.type);

  // Si el control es hijo directo y no trae id propio, se le pone el generado.
  const contenido = esControl && !idDelHijo
    ? React.cloneElement(hijo, { id })
    : children;

  const asociada = Boolean(htmlFor || idDelHijo || esControl);

  if (process.env.NODE_ENV === 'development' && !asociada) {
    // Una etiqueta con `htmlFor` que no apunta a nada es peor que una sin él:
    // aparenta estar asociada. Antes que eso, se avisa y se omite el atributo.
    console.warn(
      `Field "${label}": el hijo no es un control nativo, así que la etiqueta ` +
      'quedó sin asociar. Pasá htmlFor y repetí ese id en el input.'
    );
  }

  return (
    <div className={className}>
      <label htmlFor={asociada ? id : undefined} className={labelClassName}>
        {label}
      </label>
      {contenido}
      {hint && <p className={hintClassName}>{hint}</p>}
    </div>
  );
}
